import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { getRestaurantById } from "../api/api";
import { useAuth } from "../context/AuthContext";
import BookingForm from "../components/booking/BookingForm";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { ArrowLeft, Star } from "lucide-react";
import { Helmet } from "react-helmet";
import {Restaurant} from "../types";

const Booking = () => {
  const [match, params] = useRoute<{ id: string }>("/booking/:id");
  const restaurantId = parseInt(params?.id || "0");
  const { isAuthenticated } = useAuth();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!restaurantId) return;
      setIsLoading(true);
      try {
        const data = await getRestaurantById(restaurantId);
        setRestaurant(data);
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRestaurant();
  }, [restaurantId]);

  if (!match) {
    return null;
  }

  return (
      <>
        <Helmet>
          <title>
            {restaurant ? `Book ${restaurant.name}` : "Booking"} | SurpriseTable
          </title>
          <meta
              name="description"
              content="Book your surprise meal at one of our partner restaurants for just €15."
          />
        </Helmet>

        <section className="bg-gray-50 py-16 transition-colors duration-200 dark:bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Link href="/explore">
                <a className="inline-flex items-center text-gray-600 hover:text-primary dark:text-gray-400 dark:hover:text-primary-dark">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  <span>Back to restaurants</span>
                </a>
              </Link>
            </div>

            {isLoading ? (
                <div className="overflow-hidden rounded-xl bg-white shadow-lg dark:bg-gray-800">
                  <div className="md:flex">
                    <div className="md:w-1/2">
                      <Skeleton className="h-64 w-full md:h-full" />
                    </div>
                    <div className="p-6 md:w-1/2 md:p-8">
                      <Skeleton className="mb-4 h-8 w-48" />
                      <Skeleton className="mb-6 h-4 w-full" />
                      <Skeleton className="mb-6 h-4 w-full" />
                      <Skeleton className="mb-6 h-20 w-full" />
                      <Skeleton className="h-80 w-full" />
                    </div>
                  </div>
                </div>
            ) : error ? (
                <div className="rounded-xl bg-white p-8 text-center shadow-lg dark:bg-gray-800">
                  <h2 className="mb-4 text-2xl font-bold text-red-600 dark:text-red-400">
                    Error loading restaurant
                  </h2>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    We couldn't load the restaurant details. Please try again later.
                  </p>
                  <Button asChild>
                    <Link href="/explore">
                      <a>Back to Explore</a>
                    </Link>
                  </Button>
                </div>
            ) : restaurant ? (
                <BookingForm restaurant={restaurant} />
            ) : (
                <div className="rounded-xl bg-white p-8 text-center shadow-lg dark:bg-gray-800">
                  <h2 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                    Restaurant Not Found
                  </h2>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    We couldn't find the restaurant you're looking for.
                  </p>
                  <Button asChild>
                    <Link href="/explore">
                      <a>Browse Restaurants</a>
                    </Link>
                  </Button>
                </div>
            )}
          </div>
        </section>
      </>
  );
};

export default Booking;
